import { useMemo } from "react";

import { selectFeatureFlag } from "shared/reducers/sysReducer";
import {
  selectSortedCaseDetailSlides,
  selectSortedCaseDetailSpecimens,
} from "shared/selectors/selectSortedCaseDetail";

import { useAppSelector } from "store/hooks";

export type ViewerItem = {
  id: number;
  type: "slides" | "documents";
  specimenId?: number;
};

const useViewerItems = (caseId: number | undefined): ViewerItem[] => {
  const caseDetailId = useAppSelector(({ caseDetail }) => caseDetail?.id);
  const sortedSpecimens = useAppSelector(selectSortedCaseDetailSpecimens);
  const sortedSlides = useAppSelector(selectSortedCaseDetailSlides);

  // Check Feature Flag for non-Slide Deliverables
  const nonSlideDeliverables = useAppSelector((state) =>
    selectFeatureFlag(state, "nonSlideDeliverables")
  );

  const correctCase = !!caseId && !!caseDetailId && caseId === caseDetailId;

  return useMemo(() => {
    if (!correctCase) return [];

    const orphanedSlideViewerItems: ViewerItem[] = sortedSlides
      .filter((slide) => !slide.specimen_id)
      .map((slide) => ({
        id: slide.id || 0,
        type: "slides",
        specimenId: undefined,
      }));

    const viewerItems: ViewerItem[] = sortedSpecimens
      .flatMap((specimen) => {
        const slideItems: ViewerItem[] = sortedSlides
          .filter((slide) => slide.specimen_id === specimen.id)
          .map((slide) => ({
            id: slide.id || 0,
            type: "slides",
            specimenId: specimen.id || 0,
          }));
        const documentItems: ViewerItem[] =
          (nonSlideDeliverables &&
            specimen.case_documents?.map((caseDocument) => ({
              id: caseDocument.id || 0,
              type: "documents",
              specimenId: specimen.id || 0,
            }))) ||
          [];

        return slideItems.concat(documentItems);
      })
      .filter((viewerItem) => !!viewerItem);

    const results = orphanedSlideViewerItems.concat(viewerItems);

    return results;
  }, [correctCase, sortedSpecimens, sortedSlides, nonSlideDeliverables]);
};

export default useViewerItems;
